import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {FileTypes} from '@constants/file-types';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';

@Component({
    selector: 'scitis-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    standalone: false
})
export class FileUploadComponent implements OnInit {

  @Input()
  maxFileSize: number | undefined;

  @Input()
  isDisabled: boolean = false;

  @Input()
  title: string = 'upload';

  @Input()
  fileType: FileTypes = FileTypes.TEXT;

  @Input()
  fileTypes: FileTypes[] = [];

  @Input()
  loadingIndicatorMessage: string = '';

  @Input()
  label: string | undefined;

  @Input()
  maxImageHeight: number = 0;

  @Input()
  maxImageWidth: number = 0;

  @Output()
  fileLoadEvent: EventEmitter<string | ArrayBuffer | null> = new EventEmitter<string | ArrayBuffer | null>();

  @Output()
  fileNameLoadEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  fileLoadError: EventEmitter<ProgressEvent<FileReader>> = new EventEmitter<ProgressEvent<FileReader>>();

  @ViewChild('uploader', {static: true}) input: ElementRef | undefined;

  public fileExtensions: string = '';

  private readAsTextMimeTypesArrays: string[][] = [FileTypes.TEXT.mimeTypesArray, FileTypes.JSON.mimeTypesArray, FileTypes.CSV.mimeTypesArray];
  private readAsDataURLMimeTypesArrays: string[][] = [FileTypes.PNG.mimeTypesArray, FileTypes.JPG.mimeTypesArray, FileTypes.ICO.mimeTypesArray];

  constructor(private loadingIndicator: LoadingIndicatorService,
              private translate: TranslateService,
              private notification: NotificationService) {
  }

  ngOnInit(): void {
    this.loadingIndicatorMessage = '';
    this.fileExtensions = '';
    if (this.fileTypes && this.fileTypes.length > 0) {
      for (const fileType of this.fileTypes) {
        this.fileExtensions += fileType.extension + ',';
      }
      this.fileExtensions = this.fileExtensions.substr(0, this.fileExtensions.length - 1);
    } else {
      this.fileExtensions += this.fileType.extension;
    }
  }

  onFileSelected(event: Event) {
    if (!event.target) {
      return;
    }
    const target = event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return;
    }
    this.loadingIndicator.show(this.loadingIndicatorMessage);

    const file = target.files[0];
    if (this.isInvalidSelectedFile(file)) {
      return;
    }

    if (this.isInvalidMimeType(file)) {
      return;
    }

    if (this.isInvalidFileSize(file)) {
      return;
    }

    if (this.maxImageHeight != 0 || this.maxImageWidth != 0) {
      this.isInvalidImageDimensions(file).then(isInvalid => {
        if (isInvalid) {
          this.showWarningAboutImageFileSize(file);
        } else {
          this.loadFile(file);
        }
      });
    } else {
      this.loadFile(file);
    }

  }

  private loadFile(file: File): void {
    const fileReader = this.getFileReader(file);
    fileReader.onloadend = () => {
      this.fileLoadEvent.emit(fileReader.result);
      this.fileNameLoadEvent.emit(file.name);
      this.loadingIndicator.hide();
    };
    fileReader.onerror = (error: ProgressEvent<FileReader>) => {
      this.fileLoadError.emit(error);
      this.loadingIndicator.hide();
    };
    if (this.input) {
      this.input.nativeElement.value = '';
    }
  }

  private isInvalidSelectedFile(file: any): boolean {
    if (!file) {
      this.loadingIndicator.hide();
      return true;
    }
    return false;
  }

  private isInvalidMimeType(file: any): boolean {
    const mimeTypesArray: string[] = this.getMimeTypesArray(file.type);
    const mimeTypeNotFound = mimeTypesArray.indexOf(file.type.toLowerCase()) < 0;
    if (mimeTypeNotFound) {
      let message = this.translate.instant('Uploaded file "#0" type "#1" is not "#2". Cannot continue.');
      message = message.replace('#0', file.name);
      message = message.replace('#1', file.type);
      message = message.replace('#2', this.fileExtensions);
      this.notification.warning(message, 'File-Upload');
      this.loadingIndicator.hide();
      return true;
    }
    return false;
  }

  private isInvalidFileSize(file: any): boolean {
    if (this.maxFileSize !== undefined) {
      const maxFilesizeInKb = this.maxFileSize/1024;
      const filesizeInKb = file.size/1024;
      if (file.size > this.maxFileSize) {
        let message = this.translate.instant('The uploaded file "#1" has an invalid size of #2 which is greater than allowed size of #3');
        message = message.replace('#1', file.name);
        message = message.replace('#2', filesizeInKb + 'kb');
        message = message.replace('#3', maxFilesizeInKb + 'kb');

        this.notification.warning(message, 'File-Upload');
        this.loadingIndicator.hide();
        return true;
      }
    }
    return false;
  }

  private isInvalidImageDimensions(file: File): Promise<boolean> {
    return new Promise((resolve, _reject) => {
      const promise = this.getFileSizePromise(file);
      promise.then(image => {
        const imageDetails = image as any;
        if (imageDetails.width <= this.maxImageWidth && imageDetails.height <= this.maxImageHeight) {
          resolve(false);
        }
        resolve(true);
      }, (_err) => {
        resolve(true);
      });
    });
  }

  private getFileReader(file: File): FileReader {
    const fileReader = new FileReader();
    if (this.fileTypeFound(this.readAsTextMimeTypesArrays, file.type)) {
      fileReader.readAsText(file, 'UTF-8');

    } else if (this.fileTypeFound(this.readAsDataURLMimeTypesArrays, file.type)) {
      fileReader.readAsDataURL(file);
    }
    return fileReader;
  }

  private fileTypeFound(mimeTypesArrays: string[][], fileType: string): boolean {
    let found: boolean = false;
    mimeTypesArrays.forEach(mimeTypesArray => {
      if (mimeTypesArray.indexOf(fileType.toLowerCase()) >= 0) {
        found = true;
      }
    });
    return found;
  }

  private getMimeTypesArray(fileType: string): string[] {
    let foundMimeTypes: string[] = [];
    for (const mimeTypesArray of this.readAsTextMimeTypesArrays) {
      if (mimeTypesArray.indexOf(fileType.toLowerCase()) >= 0) {
        foundMimeTypes = mimeTypesArray;
        break;
      }
    }
    if (!foundMimeTypes || foundMimeTypes.length === 0) {
      for (const mimeTypesArray of this.readAsDataURLMimeTypesArrays) {
        if (mimeTypesArray.indexOf(fileType.toLowerCase()) >= 0) {
          foundMimeTypes = mimeTypesArray;
          break;
        }
      }
    }

    return foundMimeTypes;
  }

  private getFileSizePromise(file: File) {
    return new Promise((resolve, reject) => {
      const fileReader = this.getFileReader(file);
      fileReader.onload = (progressEvent) => { // when file has loaded
        if (progressEvent && progressEvent.target && progressEvent.target.result) {
          if (typeof progressEvent.target.result === 'string') {
            const image = new Image();
            image.src = progressEvent.target.result;
            image.onload = function (event) {
              const target = event.target as any;
              const width = target.width;
              const height = target.height;
              resolve({width, height});
            };
          } else {
            reject();
          }
        } else {
          reject();
        }
      };
    });
  }

  private showWarningAboutImageFileSize(file: File): void {
    let message = this.translate.instant('The uploaded file "#1" has an invalid dimensions. The maximum width and height is #2x#3');
    message = message.replace('#1', file.name);
    message = message.replace('#2', this.maxImageWidth);
    message = message.replace('#3', this.maxImageHeight);

    this.notification.warning(message, 'File-Upload');
    this.loadingIndicator.hide();
  }

}
